"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Button from "@/app/components/Button";
import Gate, { GateHold, useGateHold } from "@/app/components/Gate";
import { Profile } from "./characterViews";

const WORLD = "Baunheim";
/** 拡大の上限。1 が幅一杯 */
const ZOOM_MAX = 6;
const ZOOM_MIN = 1;

type Level = "near" | "world";

interface Town {
  slug: string;
  name: string;
  x: number;
  y: number;
  here: boolean;
}

interface Mark {
  slug: string;
  name: string;
  category: string;
  label: string;
  x: number;
  y: number;
}

interface MapView {
  level: Level;
  image: string;
  width: number;
  height: number;
  origin: { x: number; y: number };
  scale: number;
  current: { id: string; x: number; y: number; environment: string; nation: string | null; nationName: string | null; town: string | null };
  towns: Town[];
  marks: Mark[];
  hops: number;
}

interface PlaceDoc {
  id: string;
  title: string;
  markdown: string;
}

interface Camera {
  zoom: number;
  x: number;
  y: number;
}

const LEVELS: { level: Level; label: string }[] = [
  { level: "near", label: "近く" },
  { level: "world", label: "全体" },
];

const HOME: Camera = { zoom: 1, x: 0, y: 0 };

function clamp(value: number, low: number, high: number): number {
  return value < low ? low : value > high ? high : value;
}

/**
 * マップ画面。現在地の周りと世界全体の 2 段を切り替えて出す。
 * 絵はサーバーが PNG で描き、街の名と現在地の印はこちらで重ねる。
 * 拡大はつまむ・ホイール・ダブルクリック、移動はドラッグで行う
 */
export default function PlayMap({ region, slug, location, version }: { region: string; slug: string; location: string | null; /** 進行が動いた印。変わったら現在地の情報を読み直す */ version?: string }) {
  const [level, setLevel] = useState<Level>("near");
  const [views, setViews] = useState<Partial<Record<Level, MapView>>>({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [camera, setCamera] = useState<Camera>(HOME);
  const [frameWidth, setFrameWidth] = useState(0);
  const [place, setPlace] = useState<PlaceDoc | null>(null);
  const [placeOpen, setPlaceOpen] = useState(false);
  const [placeLoading, setPlaceLoading] = useState(false);
  const [placeMessage, setPlaceMessage] = useState("");
  // 読みの中から今の開き具合を見るための写しと、現在地の情報の読みの通し番号
  const placeOpenRef = useRef(false);
  const placeSeq = useRef(0);
  placeOpenRef.current = placeOpen;

  const frame = useRef<HTMLDivElement | null>(null);
  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const gesture = useRef<{ camera: Camera; center: { x: number; y: number }; distance: number } | null>(null);
  const moved = useRef(false);

  const view = views[level] ?? null;
  // 最初の段の地図を読み終えるまで、ロード画面に待ってもらう
  useGateHold(view === null && message === "");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setMessage("");
    const query = `world=${WORLD}&region=${encodeURIComponent(region)}&slug=${encodeURIComponent(slug)}&level=${level}`;
    fetch(`/api/play/map?${query}`)
      .then(async (response) => {
        const body = await response.json();
        if (!alive) return;
        if (response.ok) setViews((previous) => ({ ...previous, [level]: body as MapView }));
        else setMessage(body.error ?? "地図を読めません");
      })
      .catch(() => {
        if (alive) setMessage("サーバーに繋がりません");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [region, slug, level, location]);

  /** 現在地の情報を読む。後から始めた読みだけを画面へ入れる */
  const loadPlace = () => {
    const seq = ++placeSeq.current;
    setPlaceLoading(true);
    setPlaceMessage("");
    const query = `world=${WORLD}&region=${encodeURIComponent(region)}&slug=${encodeURIComponent(slug)}`;
    fetch(`/api/play/place?${query}`)
      .then(async (response) => {
        const body = await response.json();
        if (seq !== placeSeq.current) return;
        if (response.ok) setPlace(body as PlaceDoc);
        else setPlaceMessage(body.error ?? "現在地の情報を読めません");
      })
      .catch(() => {
        if (seq === placeSeq.current) setPlaceMessage("サーバーに繋がりません");
      })
      .finally(() => {
        if (seq === placeSeq.current) setPlaceLoading(false);
      });
  };

  // 現在地が変わるか進行が動いたら、抱えている md を捨てる。開いている最中は読み直す
  useEffect(() => {
    setPlace(null);
    if (placeOpenRef.current) loadPlace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, version]);

  const openPlace = () => {
    setPlaceOpen(true);
    if (place || placeLoading) return;
    loadPlace();
  };

  // 段を替えたら拡大を戻す
  useEffect(() => {
    setCamera(HOME);
  }, [level]);

  // 枠の幅を測る。名と印の位置の計算に使う
  useEffect(() => {
    const element = frame.current;
    if (!element) return;
    const measure = () => setFrameWidth(element.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [view]);

  /** 枠の大きさ。絵は枠の幅一杯に出す */
  const frameSize = useCallback(() => {
    const element = frame.current;
    if (!element || !view) return { width: 1, height: 1 };
    const width = element.clientWidth;
    return { width, height: (width * view.height) / view.width };
  }, [view]);

  /** 絵が枠から外れないように位置を収める */
  const settle = useCallback(
    (next: Camera): Camera => {
      const { width, height } = frameSize();
      const zoom = clamp(next.zoom, ZOOM_MIN, ZOOM_MAX);
      const minX = width - width * zoom;
      const minY = height - height * zoom;
      return { zoom, x: clamp(next.x, minX, 0), y: clamp(next.y, minY, 0) };
    },
    [frameSize]
  );

  /** 枠の中の点 (px, py) を中心に拡大率を factor 倍にする */
  const zoomAt = useCallback(
    (base: Camera, px: number, py: number, factor: number): Camera => {
      const zoom = clamp(base.zoom * factor, ZOOM_MIN, ZOOM_MAX);
      const ratio = zoom / base.zoom;
      return settle({ zoom, x: px - (px - base.x) * ratio, y: py - (py - base.y) * ratio });
    },
    [settle]
  );

  const onWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (!view) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const factor = event.deltaY < 0 ? 1.2 : 1 / 1.2;
    setCamera((base) => zoomAt(base, event.clientX - rect.left, event.clientY - rect.top, factor));
  };

  // ホイールで画面全体が動かないように、受け身でない形で止める
  useEffect(() => {
    const element = frame.current;
    if (!element) return;
    const stop = (event: WheelEvent) => event.preventDefault();
    element.addEventListener("wheel", stop, { passive: false });
    return () => element.removeEventListener("wheel", stop);
  }, [view]);

  const pointerPositions = (rect: DOMRect) => {
    const list = Array.from(pointers.current.values()).map((point) => ({ x: point.x - rect.left, y: point.y - rect.top }));
    if (list.length >= 2) {
      const [a, b] = list;
      return { center: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, distance: Math.hypot(a.x - b.x, a.y - b.y) };
    }
    return { center: list[0] ?? { x: 0, y: 0 }, distance: 0 };
  };

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!view) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const rect = event.currentTarget.getBoundingClientRect();
    gesture.current = { camera, ...pointerPositions(rect) };
    moved.current = false;
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(event.pointerId) || !gesture.current) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const rect = event.currentTarget.getBoundingClientRect();
    const now = pointerPositions(rect);
    const start = gesture.current;
    const dx = now.center.x - start.center.x;
    const dy = now.center.y - start.center.y;
    if (Math.abs(dx) + Math.abs(dy) > 3) moved.current = true;
    if (pointers.current.size >= 2 && start.distance > 0) {
      // つまむ：始めの 2 点の中心を軸に、距離の比で拡大する
      const factor = now.distance / start.distance;
      const scaled = zoomAt(start.camera, start.center.x, start.center.y, factor);
      setCamera(settle({ ...scaled, x: scaled.x + dx, y: scaled.y + dy }));
    } else {
      setCamera(settle({ ...start.camera, x: start.camera.x + dx, y: start.camera.y + dy }));
    }
  };

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(event.pointerId);
    if (pointers.current.size === 0) {
      gesture.current = null;
      return;
    }
    // 指が 1 本残ったら、その位置からドラッグを続ける
    const rect = event.currentTarget.getBoundingClientRect();
    gesture.current = { camera, ...pointerPositions(rect) };
  };

  const onDoubleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!view) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setCamera((base) => (base.zoom >= ZOOM_MAX ? HOME : zoomAt(base, event.clientX - rect.left, event.clientY - rect.top, 2)));
  };

  // 名と印は絵の層の外に置き、位置だけを絵に合わせて計算する。層ごと引き伸ばすと字がぼやける
  const frameHeight = view ? (frameWidth * view.height) / view.width : 0;
  const at = (x: number, y: number) =>
    view
      ? { left: `${camera.x + (x / view.width) * frameWidth * camera.zoom}px`, top: `${camera.y + (y / view.height) * frameHeight * camera.zoom}px` }
      : {};
  // 大きさは拡大率の平方根の分だけ増やす。絵ほどは大きくならず、小さくもならない
  const grow = `translate(-50%, -50%) scale(${Math.sqrt(camera.zoom)})`;

  return (
    <div className="pl-tabbed">
      <div className="pl-title">
        <h2>マップ</h2>
        <span>{view ? `${view.current.nationName ? `${view.current.nationName}の` : ""}${view.current.environment}` || location || "—" : location ?? "—"}</span>
      </div>

      <div className="pl-tabs pl-tabs-folder" role="tablist" aria-label="地図の段">
        {LEVELS.map((one) => (
          <button
            key={one.level}
            type="button"
            role="tab"
            aria-selected={level === one.level}
            className={level === one.level ? "pl-tab pl-tab-on" : "pl-tab"}
            onClick={() => setLevel(one.level)}
          >
            {one.label}
          </button>
        ))}
      </div>

      <div className="pl-folder-body pl-map-body">
      {/* 段を替えるたびに、その段の地図と絵が出せるまでロード画面を挟む */}
      <Gate key={level} variant="inline">
      <GateHold busy={view === null && message === ""} />
      {view ? (
        <div
          ref={frame}
          className={camera.zoom > 1 ? "pl-map pl-map-zoomed" : "pl-map"}
          style={{ aspectRatio: `${view.width} / ${view.height}` }}
          onWheel={onWheel}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDoubleClick={onDoubleClick}
        >
          <div className="pl-map-layer" style={{ transform: `translate(${camera.x}px, ${camera.y}px) scale(${camera.zoom})` }}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img className="pl-map-img" src={view.image} width={view.width} height={view.height} alt="" draggable={false} />
          </div>
          {frameWidth > 0 ? (
            <div className="pl-map-labels">
              {view.marks.map((mark) => (
                <span
                  key={mark.slug}
                  className={`pl-map-mark pl-map-mark-${mark.category}`}
                  style={{ ...at(mark.x, mark.y), transform: grow }}
                  title={mark.label}
                >
                  <i />
                  <b>{mark.name}</b>
                </span>
              ))}
              {view.towns.map((town) => (
                <span
                  key={town.slug}
                  className={town.here ? "pl-map-town pl-map-town-here" : "pl-map-town"}
                  style={{ ...at(town.x, town.y), transform: grow }}
                >
                  <i />
                  <b>{town.name}</b>
                </span>
              ))}
              <span className="pl-map-here" style={{ ...at(view.current.x, view.current.y), transform: grow }} />
            </div>
          ) : null}
        </div>
      ) : (
        <div className="pl-map pl-map-empty">{loading ? "地図を描いています" : message || "地図を読めません"}</div>
      )}

      {view ? (
        <div className="pl-map-info">
          <Button onClick={openPlace}>現在地情報</Button>
        </div>
      ) : null}
      {message && view ? <p className="pl-message">{message}</p> : null}
      </Gate>
      </div>

      {placeOpen ? (
        <div className="pl-modal-back pl-modal-back-center" onClick={() => setPlaceOpen(false)}>
          <div className="pl-modal pl-modal-center" role="dialog" aria-label="現在地情報" onClick={(event) => event.stopPropagation()}>
            <div className="pl-modal-head">
              <h3>{place?.title ?? "現在地情報"}</h3>
              <button type="button" className="pl-modal-x" aria-label="閉じる" onClick={() => setPlaceOpen(false)}>
                ×
              </button>
            </div>
            <div className="pl-modal-body">
              {place ? <Profile text={place.markdown} /> : <p className="pl-note">{placeLoading ? "読み込み中・・・" : placeMessage || "現在地の情報を読めません"}</p>}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
